// Readable text for the reconciled-month lock (409) and for import rows that
// were skipped because their month is reconciled (migration 20261125).
import { formatApiError } from '../../utils/errorMessage';

export const RECONCILED_DEFAULT = 'This month is reconciled. Reopen it to change or delete transactions.';

// The server's lock message names the account and month; anything else on a
// 409 lock (no text, raw text) becomes the default sentence.
export function reconciledLockMessage(error) {
  const res = error && error.response;
  if (!res || res.status !== 409) return '';
  const data = res.data && typeof res.data === 'object' ? res.data : {};
  const text = typeof data.error === 'string' ? data.error : (typeof data.message === 'string' ? data.message : '');
  const isLock = data.code === 'RECONCILED_LOCKED' || /reconciled/i.test(text);
  if (!isLock) return '';
  // The full server text is kept (it names the account and month); nothing is cut off.
  return /reopen/i.test(text) ? text : RECONCILED_DEFAULT;
}

// The message to show for a failed edit, delete or import.
export function transactionErrorMessage(error, fallback) {
  return reconciledLockMessage(error) || formatApiError(error, fallback);
}

const plural = (n, one, many) => (n === 1 ? one : many);

// "2-5, 9" for row numbers, so a long list stays readable.
export function rowRanges(rows) {
  const nums = rows.map(Number);
  if (!nums.length || nums.some((n) => !Number.isInteger(n))) return rows.join(', ');
  const sorted = [...new Set(nums)].sort((a, b) => a - b);
  const parts = [];
  let start = sorted[0]; let prev = sorted[0];
  for (const n of sorted.slice(1).concat([null])) {
    if (n !== null && n === prev + 1) { prev = n; continue; }
    parts.push(start === prev ? String(start) : (prev === start + 1 ? `${start}, ${prev}` : `${start}-${prev}`));
    start = n; prev = n;
  }
  return parts.join(', ');
}

// Summary after an Excel import, including rows skipped for a reconciled month.
export function importSummary(data = {}) {
  const imported = Number(data.imported) || 0;
  const locked = Array.isArray(data.reconciled_rows) ? data.reconciled_rows : [];
  const dupes = Math.max(0, (Number(data.skipped) || 0) - locked.length);
  const autoTransfers = Number(data.auto_transfers) || 0;
  let text = `Imported ${imported} ${plural(imported, 'transaction', 'transactions')}.`;
  if (dupes) text += ` ${dupes === 1 ? '1 was' : `${dupes} were`} already in this account and ${plural(dupes, 'was', 'were')} skipped.`;
  if (locked.length) {
    const months = [...new Set(locked.map((r) => r.month).filter(Boolean))].join(', ');
    const rows = rowRanges(locked.map((r) => r.row));
    text += ` ${locked.length} ${plural(locked.length, 'row is', 'rows are')} dated in a reconciled month${months ? ` (${months})` : ''} and ${plural(locked.length, 'was', 'were')} not imported (${plural(locked.length, 'row', 'rows')} ${rows}). Reopen the month to add ${plural(locked.length, 'it', 'them')}.`;
  }
  if (autoTransfers) text += ` ${autoTransfers} card payment or transfer ${plural(autoTransfers, 'row was', 'rows were')} marked as Transfer.`;
  return text;
}
